import { createReducer } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { Roster, RosterExpandable } from '../../Components/Modals/models';
import { RostersState } from './models';
import * as RostersActions from './rosters.action';

const INITIAL_STATE: RostersState = {
  rosterList: [],
  loaded: false,
  loading: false,
  errMsg: ''
};

export const rostersReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase(RostersActions.fetchRostersStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.fetchRostersSuccess, (state, action) => {
      let rosterList: RosterExpandable[];

      // a useless check, it's already guarded in the sagas
      if (action.payload.success) {

        if (action.payload.createdRosterID) {

          // first roster created
          if (state.rosterList.length === 0) {
            // no need to sort
            rosterList = action.payload.rosterList
              .map((item:Roster): RosterExpandable => ({
                ...item,
                isExpanded: false
              }));
          } else {
            // rosterList is already populated and sorted, just push the new one to the back of array
            let createdRoster = action.payload.rosterList.find((rts) => rts.rosterID == action.payload.createdRosterID);
            rosterList = cloneDeep(state.rosterList);
            if (createdRoster) {
              createdRoster = {
                ...createdRoster,
                isExpanded: false,
                children: []
              } as RosterExpandable;
              rosterList.unshift(<RosterExpandable>createdRoster);
            }
          }

        }
        else if (action.payload.updatedRosterID) {
          const updatedRoster = action.payload.rosterList.find(rts => rts.rosterID == action.payload.updatedRosterID);

          // updatedRoster should always be defined; else statement should never be hit
          if (updatedRoster) {
            rosterList = cloneDeep(state.rosterList).map((rts): RosterExpandable => {
              if (rts.rosterID == action.payload.updatedRosterID) {
                return {
                  isExpanded: false,
                  children: [],
                  ...updatedRoster
                };
              } else {
                return rts;
              }
            });
          } else {
            rosterList = cloneDeep(state.rosterList);
          }
        } else {
          // should be first fetch on sign in
          // sorting is handled in the BE
          rosterList = action.payload.rosterList
            .map((item:Roster): RosterExpandable => ({
              ...item,
              isExpanded: false,
              children: []
            }));
        }
      } else {
        rosterList = [] as RosterExpandable[];
      }

      return {
        ...state,
        loaded: true,
        loading: false,
        rosterList
      };
    })
    .addCase(RostersActions.fetchRostersFailure, (state, action) => ({
      ...state,
      loaded: false,
      loading: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.addRosterStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.addRosterFailure, (state, action) => ({
      ...state,
      loading: false,
      loaded: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.updateRosterStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.updateRosterFailure, (state, action) => ({
      ...state,
      loading: false,
      loaded: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.deleteRosterStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.deleteRosterSuccess, (state, action) => {
      const rosterList = cloneDeep(state.rosterList).filter(rts => rts.rosterID != action.payload.rosterID);
      return {
        ...state,
        loading: false,
        loaded: true,
        rosterList
      };
    })
    .addCase(RostersActions.deleteRosterFailure, (state, action) => ({
      ...state,
      loading: false,
      loaded: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.fetchRosterStudentsStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.fetchRosterStudentsSuccess, (state, action) => {
      const rosterList = cloneDeep(state.rosterList).map(rts => {
        // there must always be at least one student
        if (rts.rosterID == action.payload.studentInfoList[0].rosterId) {
          rts.children = action.payload.studentInfoList;
          if (action.payload.expand) {
            rts.isExpanded = true;
          }
        }
        return rts;
      });
      return {
        rosterList,
        loading: false,
        loaded: true,
        errMsg: ''
      };
    })
    .addCase(RostersActions.fetchRosterStudentsFailure, (state, action) => ({
      ...state,
      loading: false,
      loaded: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.toggleRoster, (state, action) => {
      const rosterList = cloneDeep(state.rosterList).map(rts => {
        if (rts.rosterID == action.payload.rosterID) {
          rts.isExpanded = !rts.isExpanded;
        }
        return rts;
      });
      return {
        ...state,
        rosterList
      };
    })
    .addCase(RostersActions.deleteRosterStudentStart, (state) => ({
      ...state,
      loading: true,
      loaded: false,
      errMsg: ''
    }))
    .addCase(RostersActions.deleteRosterStudentSuccess, (state, action) => {
      const rosterList = cloneDeep(state.rosterList).map(rts => {
        if (rts.rosterID == action.payload.rosterID && rts.children) {
          rts.children = rts.children.filter(child => child.studentID !== action.payload.studentID);
        }
        return rts;
      });
      return {
        rosterList,
        loading: false,
        loaded: true,
        errMsg: ''
      };
    })
    .addCase(RostersActions.deleteRosterStudentFailure, (state, action) => ({
      ...state,
      loading: false,
      loaded: false,
      errMsg: action.payload.errMsg
    }))
    .addCase(RostersActions.resetRosters, () => ({
      rosterList: [],
      loaded: false,
      loading: false,
      errMsg: ''
    }));
});

export default rostersReducer;